import React, { useState, useEffect } from 'react';
import Select from '../../components/ui/Select';
import EmptyState from '../../components/ui/EmptyState';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';

import Icon from '../../components/AppIcon';
import reviewerService from '../../services/reviewerService';
import AssignmentCard from './components/AssignmentCard';
import WorkloadStats from './components/WorkloadStats';
import DeadlineCalendar from './components/DeadlineCalendar';
import BadgeDownloadButton from '../../components/badge/BadgeDownloadButton';

const ReviewerDashboard = () => {
  const { t } = useTranslation('reviews');
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [workloadStats, setWorkloadStats] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Reached from the conference bar, this screen scopes to that conference.
  // Reached from global navigation, it shows every assignment. Same screen,
  // same data, one honest filter — rather than two near-identical routes.
  const [searchParams] = useSearchParams();
  const scopedConferenceId = searchParams?.get('conferenceId') || null;
  const [sortBy, setSortBy] = useState('deadline');

  useEffect(() => {
    if (!user) navigate('/user-sign-in');
  }, [user, navigate]);

  useEffect(() => {
    if (user) loadReviewerData();
  }, [user]);

  const loadReviewerData = async () => {
    try {
      setLoading(true);
      setError('');
      const [assignedPapers, stats, upcomingDeadlines] = await Promise.all([
        reviewerService?.getAssignedPapers(user?.id),
        reviewerService?.getWorkloadStats(user?.id),
        reviewerService?.getUpcomingDeadlines(user?.id)
      ]);
      setAssignments(assignedPapers);
      setWorkloadStats(stats);
      setDeadlines(upcomingDeadlines);
    } catch (err) {
      console.error('Load reviewer data error:', err);
      setError(err?.message || 'Failed to load reviewer data');
    } finally {
      setLoading(false);
    }
  };

  const filteredAssignments = assignments?.filter(assignment => {
    if (scopedConferenceId && String(assignment?.conferenceId) !== String(scopedConferenceId)) return false;
    if (filterStatus === 'all') return true;
    if (filterStatus === 'pending') return assignment?.isDraft;
    if (filterStatus === 'completed') return !assignment?.isDraft;
    return true;
  });

  // Within a group, honour whatever the reviewer chose to sort by.
  const bySelectedKey = (a, b) => {
    if (sortBy === 'deadline') return new Date(a?.reviewDeadline) - new Date(b?.reviewDeadline);
    if (sortBy === 'conference') return (a?.conferenceName || '')?.localeCompare(b?.conferenceName || '');
    if (sortBy === 'status') return (a?.status || '')?.localeCompare(b?.status || '');
    return 0;
  };

  // Outstanding work first, finished work last — a submitted review is a
  // record, not a task, and it shouldn't sit between two papers that still need
  // reading. Rendered as two labelled groups rather than one run, so the
  // boundary is visible instead of implied by ordering alone.
  const outstanding = filteredAssignments?.filter(a => a?.isDraft)?.sort(bySelectedKey) || [];
  const done = filteredAssignments?.filter(a => !a?.isDraft)?.sort(bySelectedKey) || [];
  const sortedAssignments = [...outstanding, ...done];

  const priorityAssignments = sortedAssignments?.filter(assignment => {
    if (assignment?.isDraft) {
      const daysLeft = Math.ceil((new Date(assignment?.reviewDeadline) - new Date()) / (1000 * 60 * 60 * 24));
      return daysLeft <= 7;
    }
    return false;
  });

  const handleStartReview = (assignmentId) => {
    const assignment = assignments?.find(a => a?.reviewId === assignmentId);
    if (assignment?.submissionId) {
      navigate(`/streamlined-review-workspace?submissionId=${assignment.submissionId}`);
    } else {
      navigate(`/streamlined-review-workspace`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="flex items-center justify-center h-[calc(100vh-48px)]">
          <Icon name="Loader" size={24} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background bg-mesh">
      <UniversalNavbar />
      <main className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="page-shell">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground tracking-tight">{t('reviewerDashboard.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('reviewerDashboard.subtitle')}</p>
          </div>

          {error && (
            <div className="bg-error-subtle border border-error-border text-error px-3 py-2 rounded-md mb-4 flex items-center gap-2 text-sm">
              <Icon name="AlertCircle" size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Urgency banner stays at the top — it demands an action. The
              workload counters moved BELOW the queue: a reviewer opens this
              page to see what to review next, and six figures were pushing that
              list off the fold. */}
          {priorityAssignments?.length > 0 && (
            <div className="bg-warning-subtle border border-warning-border rounded-md px-3 py-2.5 mb-4 flex items-center gap-2.5">
              <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0" />
              <p className="text-sm text-foreground">
                <span className="font-medium">{t('reviewerDashboard.urgentReviews', { count: priorityAssignments?.length })}</span>
                {' '}— {t('reviewerDashboard.urgentDueMessage')}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
            <div className="bg-card border border-border overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40">
                <h2 className="text-sm font-semibold text-foreground">{t('reviewerDashboard.assignedPapers')}</h2>
                <div className="flex items-center gap-2">
                  <Select
                    value={filterStatus}
                    onChange={(value) => setFilterStatus(value)}
                    className="min-w-[9rem]"
                    options={[
                      { value: 'all', label: t('reviewerDashboard.filters.all', { count: assignments?.length }) },
                      { value: 'pending', label: t('reviewerDashboard.filters.pending', { count: workloadStats?.pending }) },
                      { value: 'completed', label: t('reviewerDashboard.filters.completed', { count: workloadStats?.completed }) },
                    ]}
                  />
                  <Select
                    value={sortBy}
                    onChange={(value) => setSortBy(value)}
                    className="min-w-[9rem]"
                    options={[
                      { value: 'deadline', label: t('reviewerDashboard.sort.byDeadline') },
                      { value: 'conference', label: t('reviewerDashboard.sort.byConference') },
                      { value: 'status', label: t('reviewerDashboard.sort.byStatus') },
                    ]}
                  />
                </div>
              </div>

              <div className="divide-y divide-border">
                {sortedAssignments?.length === 0 ? (
                  <EmptyState
                    compact
                    filtered={filterStatus !== 'all' || Boolean(scopedConferenceId)}
                    onClear={() => { setFilterStatus('all'); navigate('/reviewer-dashboard'); }}
                    icon="ClipboardCheck"
                    title={filterStatus === 'all'
                      ? t('reviewerDashboard.emptyState.noPapers')
                      : t('reviewerDashboard.emptyState.noFilteredReviews')}
                    body={filterStatus === 'all'
                      ? t('reviewerDashboard.emptyState.noPapersBody', 'Papers appear here once a chair assigns them to you.')
                      : undefined}
                  />
                ) : (
                  <>
                    {outstanding?.length > 0 && (
                      <>
                        {done?.length > 0 && (
                          <div className="px-4 py-1.5 bg-muted/30 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {t('reviewerDashboard.groups.outstanding', { count: outstanding.length, defaultValue: 'Still to review ({{count}})' })}
                          </div>
                        )}
                        {outstanding.map(assignment => (
                          <AssignmentCard
                            key={assignment?.reviewId}
                            assignment={assignment}
                            onStartReview={handleStartReview}
                          />
                        ))}
                      </>
                    )}
                    {done?.length > 0 && (
                      <>
                        <div className="px-4 py-1.5 bg-muted/30 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {t('reviewerDashboard.groups.done', { count: done.length, defaultValue: 'Submitted ({{count}})' })}
                        </div>
                        {/* Dimmed: still readable, no longer competing with work
                            that hasn't been done. */}
                        <div className="opacity-70">
                          {done.map(assignment => (
                            <AssignmentCard
                              key={assignment?.reviewId}
                              assignment={assignment}
                              onStartReview={handleStartReview}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <DeadlineCalendar deadlines={deadlines} />

              {workloadStats?.conferenceWorkload?.length > 0 && (
                <div className="bg-card border border-border overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-border">
                    <h3 className="text-sm font-semibold text-foreground">{t('reviewerDashboard.sort.byConference')}</h3>
                  </div>
                  <div className="p-3 space-y-3">
                    {workloadStats?.conferenceWorkload?.map(conf => (
                      <div key={conf?.conferenceId}>
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <p className="text-xs font-medium text-foreground truncate">{conf?.conferenceName}</p>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-xs text-muted-foreground">{conf?.completed}/{conf?.total}</span>
                            <BadgeDownloadButton conferenceId={conf?.conferenceId} compact />
                          </div>
                        </div>
                        <div className="w-full bg-muted h-1.5">
                          <div
                            className="bg-primary h-1.5 transition-all"
                            style={{ width: `${(conf?.completed / conf?.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {workloadStats && <WorkloadStats stats={workloadStats} />}
        </div>
      </main>
    </div>
  );
};

export default ReviewerDashboard;
